// src/hooks/useInactivityMonitor.ts

import { useEffect, useRef, useCallback } from 'react';
import { INACTIVITY_CONFIG } from '../config/app.config';

/**
 * Custom hook for monitoring user inactivity
 * Tracks user activity and triggers callbacks after specified periods of inactivity
 */

interface UseInactivityMonitorOptions {
  isActive: boolean; // Only monitor when user is authenticated
  onWarning: () => void; // Called when warning time is reached
  onLogout: () => void; // Called when logout time is reached
}

export const useInactivityMonitor = ({
  isActive,
  onWarning,
  onLogout,
}: UseInactivityMonitorOptions) => {
  const warningTimerRef = useRef<NodeJS.Timeout | null>(null);
  const logoutTimerRef = useRef<NodeJS.Timeout | null>(null);
  const isWarningShownRef = useRef<boolean>(false);

  // Clear all timers
  const clearTimers = useCallback(() => {
    if (warningTimerRef.current) {
      clearTimeout(warningTimerRef.current);
      warningTimerRef.current = null;
    }
    if (logoutTimerRef.current) {
      clearTimeout(logoutTimerRef.current);
      logoutTimerRef.current = null;
    }
    isWarningShownRef.current = false;
  }, []);

  // Reset timers on user activity
  const resetTimers = useCallback(() => {
    // Clear existing timers
    clearTimers();

    // Only set timers if monitoring is active
    if (!isActive) return;

    // Set warning timer (4 minutes)
    warningTimerRef.current = setTimeout(() => {
      console.log('InactivityMonitor: Warning time reached');
      isWarningShownRef.current = true;
      onWarning();
    }, INACTIVITY_CONFIG.WARNING_TIME);

    // Set logout timer (5 minutes)
    logoutTimerRef.current = setTimeout(() => {
      console.log('InactivityMonitor: Logout time reached');
      onLogout();
    }, INACTIVITY_CONFIG.LOGOUT_TIME);
  }, [isActive, onWarning, onLogout, clearTimers]);

  // Handle user activity
  const handleActivity = useCallback(() => {
    // Only reset if monitoring is active
    if (!isActive) return;

    console.log('InactivityMonitor: User activity detected, resetting timers');
    resetTimers();
  }, [isActive, resetTimers]);

  // Set up event listeners
  useEffect(() => {
    if (!isActive) {
      clearTimers();
      return;
    }

    console.log('InactivityMonitor: Starting activity monitoring');

    // List of events to monitor
    const events = ['mousemove', 'keydown', 'click', 'touchstart', 'scroll'];

    // Add event listeners
    events.forEach(event => {
      document.addEventListener(event, handleActivity, { passive: true });
    });

    // Start initial timers
    resetTimers();

    // Cleanup function
    return () => {
      console.log('InactivityMonitor: Cleaning up event listeners');
      events.forEach(event => {
        document.removeEventListener(event, handleActivity);
      });
      clearTimers();
    };
  }, [isActive, handleActivity, resetTimers, clearTimers]);

  // Reset timers when isActive changes
  useEffect(() => {
    if (isActive) {
      resetTimers();
    } else {
      clearTimers();
    }
  }, [isActive, resetTimers, clearTimers]);

  // Return functions for manual control
  return {
    resetTimers,
    clearTimers,
    isWarningShown: isWarningShownRef.current,
  };
};

export default useInactivityMonitor;
